import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * PortfolioInfoCards Component
 * 
 * Compact display of portfolio information without separate cards
 */
const PortfolioInfoCards = ({ portfolio }) => {
  const { t } = useTranslation();

  if (!portfolio) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Construction Info - Compact Single Box */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1.5, color: 'text.primary' }}>
          {t('riskManagement.portfolioConstruction.overview.construction')}
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: 0.5,
          bgcolor: 'white',
          borderRadius: 1.5,
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* Row 1 Col 1: Date */}
          <Box sx={{ p: 1.5, borderRight: '1px solid rgba(0,0,0,0.05)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
              {t('riskManagement.portfolioConstruction.overview.constructionDate')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', mt: 0.25 }}>
              {portfolio.construction_date || portfolio.metadata?.construction_date || new Date().toLocaleDateString('vi-VN')}
            </Typography>
          </Box>
          
          {/* Row 1 Col 2: Stock Exposure */}
          <Box sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
              {t('riskManagement.portfolioConstruction.overview.stockExposure')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2196f3', fontSize: '1.1rem', mt: 0.25 }}>
              {((portfolio.summary?.total_allocated || portfolio.portfolio?.stock_exposure || 0) * 100).toFixed(0)}%
            </Typography>
          </Box>
          
          {/* Row 2 Col 1: Allocated Now */}
          <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
              {t('riskManagement.portfolioConstruction.overview.allocatedNow')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#4caf50', fontSize: '1.1rem', mt: 0.25 }}>
              {((portfolio.summary?.allocated_now || portfolio.summary?.buy_now_allocated || 0) * 100).toFixed(0)}%
            </Typography>
          </Box>
          
          {/* Row 2 Col 2: Cash Reserved */}
          <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
              {t('riskManagement.portfolioConstruction.overview.cashReserved')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2196f3', fontSize: '1.1rem', mt: 0.25 }}>
              {((portfolio.summary?.cash_reserved || (1 - (portfolio.summary?.total_allocated || 0))) * 100).toFixed(0)}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* AI-Adjusted Exposure - Compact Single Box */}
      {portfolio.market_breadth && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
              {t('riskManagement.portfolioConstruction.overview.aiAdjustedExposure')}
            </Typography>
            <Chip 
              label={`${t('riskManagement.portfolioConstruction.overview.riskScore')}: ${portfolio.market_breadth.risk_score?.toFixed(1) || 'N/A'}/100`}
              size="small"
              sx={{ 
                bgcolor: 'rgba(102,126,234,0.1)',
                color: '#667eea',
                fontWeight: 700,
                height: 22,
                fontSize: '0.7rem',
              }}
            />
          </Box>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr', 
            gap: 0.5,
            bgcolor: 'white',
            borderRadius: 1.5,
            border: '1px solid rgba(0,0,0,0.08)',
            overflow: 'hidden',
            mb: 1
          }}>
            <Box sx={{ p: 1.5, borderRight: '1px solid rgba(0,0,0,0.05)', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
                {t('riskManagement.portfolioConstruction.overview.regimeExposure')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#667eea', fontSize: '1.5rem', mt: 0.5 }}>
                {((portfolio.market_breadth.regime_exposure || 0) * 100).toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, borderRight: '1px solid rgba(0,0,0,0.05)', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
                {t('riskManagement.portfolioConstruction.overview.breadthExposure')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50', fontSize: '1.5rem', mt: 0.5 }}>
                {((portfolio.market_breadth.breadth_exposure || 0) * 100).toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, textAlign: 'center', bgcolor: 'rgba(255,152,0,0.03)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', textTransform: 'uppercase', display: 'block' }}>
                {t('riskManagement.portfolioConstruction.overview.finalExposure')}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#ff9800', fontSize: '1.75rem', mt: 0.5 }}>
                {((portfolio.market_breadth.final_exposure || 0) * 100).toFixed(0)}%
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', fontStyle: 'italic', fontSize: '0.7rem' }}>
            {t('riskManagement.portfolioConstruction.overview.exposureFormula')}
          </Typography>
        </Box>
      )}

      {/* Parameters - Inline */}
      {portfolio.parameters && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1.5, color: 'text.primary' }}>
            {t('riskManagement.portfolioConstruction.overview.parameters')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5 }}>
            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.overview.topSectors')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {portfolio.parameters.top_sectors}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.overview.stocksPerSector')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {portfolio.parameters.stocks_per_sector}
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.overview.capSingle')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {(portfolio.parameters.cap_single * 100).toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.overview.capSector')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {(portfolio.parameters.cap_sector * 100).toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                {t('riskManagement.portfolioConstruction.overview.lookback')}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {portfolio.parameters.lookback_days}
                <Typography component="span" variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                  days
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PortfolioInfoCards;
